// src/pages/projects/components/AddProjectPlanModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Select, Checkbox, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ProjectClientDto, ProjectPlanPriority, CreateProjectPlanCommand } from '../../../api/models';
import { ProjectsApi } from '../../../api/api';
import { formatSquareMeters } from 'utility/utility';

const projectsApi = new ProjectsApi();

interface Props {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateProjectPlanCommand) => Promise<void>;
  projectId: number;
}

const AddProjectPlanModal: React.FC<Props> = ({ visible, onCancel, onSubmit, projectId }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isClientSpecific, setIsClientSpecific] = useState(false);
  const [clients, setClients] = useState<ProjectClientDto[]>([]);
  const [selectedClient, setSelectedClient] = useState<ProjectClientDto | null>(null);

  useEffect(() => {
    if (visible && isClientSpecific) {
      loadClients();
    }
  }, [visible, isClientSpecific]);

  const loadClients = async () => {
    try {
      const response = await projectsApi.apiProjectsIdClientsGet({ id: projectId });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleClientChange = (clientId: number) => {
    const client = clients.find(c => c.clientId === clientId);
    setSelectedClient(client || null);
    form.setFieldValue('unitId', undefined);
  };

  return (
    <Modal title={t('projects:add-plan', 'Add New Plan')} open={visible} onCancel={onCancel} onOk={() => form.submit()}>
      <Form form={form} layout="vertical" onFinish={onSubmit} initialValues={{ priority: ProjectPlanPriority.Medium }}>
        <Form.Item>
          <Checkbox onChange={e => setIsClientSpecific(e.target.checked)}>
            {t('projects:client-specific-plan', 'This plan is specific to a client')}
          </Checkbox>
        </Form.Item>

        {isClientSpecific && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item
              name="clientId"
              label={t('projects:select-client', 'Select Client')}
              rules={[{ required: true }]}
            >
              <Select
                onChange={handleClientChange}
                options={clients.map(client => ({
                  value: client.clientId,
                  label: client.clientName,
                }))}
              />
            </Form.Item>

            <Form.Item name="unitId" label={t('projects:select-unit', 'Select Unit')} rules={[{ required: true }]}>
              <Select
                disabled={!selectedClient}
                options={selectedClient?.dealUnits?.map(unit => ({
                  value: unit.id,
                  label: `${unit.name} (${formatSquareMeters(unit.squareMeters!)}) - ${unit.categoryName}`,
                }))}
              />
            </Form.Item>
          </Space>
        )}

        <Form.Item name="title" label={t('projects:plan-title', 'Title')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item name="description" label={t('projects:description', 'Description')} rules={[{ required: true }]}>
          <Input.TextArea />
        </Form.Item>

        <Form.Item name="dueDate" label={t('projects:due-date', 'Due Date')}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="priority" label={t('projects:priority', 'Priority')}>
          <Select
            options={Object.entries(ProjectPlanPriority).map(([key, value]) => ({
              value,
              label: (
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      marginRight: '8px',
                      backgroundColor:
                        key === 'High'
                          ? '#ff4d4f'
                          : key === 'Medium'
                          ? '#faad14'
                          : key === 'Low'
                          ? '#52c41a'
                          : '#d9d9d9',
                    }}
                  />
                  {t(`projects:priority-${key.toLowerCase()}`, key)}
                </span>
              ),
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddProjectPlanModal;
